import axios, {type AxiosError} from 'axios';
import Cookies from 'js-cookie';
import {BASE_URL} from 'utils/AppConfig';
import {variables} from 'utils/constant';

const instance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Access-Control-Allow-Origin': '*',
    'Content-Type': 'application/json',
  },
});

// Maintain an active request counter
let activeRequests = 0;

const isListEventEndpoint = (url: string | undefined): boolean => {
  const excludedEndpoints = [
    '/api/list-event',
    '/api/update-device-token',
    '/api/new-user-registration-chart',
    '/api/get-venue-performance-chart',
    '/api/get-event-engagement-chart',
    '/api/new-venue-registration-chart',
    '/api/dashboard-count',
  ];

  // Remove query parameters before checking
  const cleanUrl = url?.split('?')[0] ?? '';
  return excludedEndpoints.includes(cleanUrl);
};

// Function to show loader
const showLoader = (): void => {
  const loader: HTMLElement | null = document.getElementById('cover-spin');
  if (loader != null) loader.style.display = 'block';
};

// Function to hide loader
const hideLoader = (): void => {
  const loader: HTMLElement | null = document.getElementById('cover-spin');
  if (loader != null && activeRequests === 0) {
    loader.style.display = 'none';
  }
};

instance.interceptors.request.use(
  async (config: any) => {
    const authDetails = Cookies.get(variables.ecommerce_admin);

    if (
      config.url === '/api/create-or-update-venue' ||
      config.url === '/api/create-or-update-event' ||
      config.url === '/api/common/create-product' ||
      config.url === '/api/create-or-update-club-profile' ||
      config.url === '/api/create-or-update-restaurant-profile'
    ) {
      config.headers['Content-Type'] = 'multipart/form-data';
    } else {
      config.headers['Content-Type'] = 'application/json';
    }

    if (authDetails != null) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${authDetails}`,
      };
    }

    // Increment request counter
    if (!isListEventEndpoint(config.url)) {
      activeRequests++;
      showLoader();
    }

    return config;
  },
  async (error: AxiosError) => {
    return await Promise.reject(error);
  },
);

// Response Interceptor
instance.interceptors.response.use(
  response => {
    if (!isListEventEndpoint(response.config.url)) {
      activeRequests = Math.max(activeRequests - 1, 0); // Ensure counter doesn't go negative
      hideLoader();
    }
    return response;
  },
  async (error: AxiosError) => {
    if (!isListEventEndpoint(error.config?.url)) {
      activeRequests = Math.max(activeRequests - 1, 0);
      hideLoader();
    }

    if (error?.response?.status === 401 || error?.response?.status === 405) {
      Cookies.remove(variables.ecommerce_admin);
      window.location.href = '/';
    }

    return await Promise.reject(error);
  },
);

export default instance;
